import {Location} from 'history';
import qs from 'query-string';

interface To {
  pathname: string;
  search?: string;
}

/**
 * generateRouteHref returns a string including URL query parameters to be used in a link
 * @param toOrPath - Path or To object that the link should go to (relative to the root of application)
 * @param location - Location object representing the current location of the app
 * @returns - String of where the link should navigate to - e.g. /crew?lang=fr
 */
export function generateRouteHref(toOrPath: To, location: Location) {
  const to = normalizeTo(toOrPath);
  const queries = constructQueryParams(to.search ?? '', location, ['lang']);
  if (Object.keys(queries).length > 0) {
    return `${to.pathname}?${qs.stringify(queries)}`;
  }
  return typeof to === 'string' ? to : to.pathname;
}

/**
 * generateRouteTo returns a Location object to be used in a Link
 * @param toOrPath - Path or To object that the link should go to (relative to the root of application)
 * @param location - Location object representing the current location of the app
 * @returns - Location object of where the application should go to - e.g. `{pathname: '/crew', search: '?lang=fr'}'
 */
export function generateRouteTo(toOrPath: To, location: Location) {
  const to = normalizeTo(toOrPath);
  const queries = constructQueryParams(to.search ?? '', location, ['lang']);
  if (Object.keys(queries).length > 0) {
    return {
      pathname: to.pathname,
      search: `?${qs.stringify(queries)}`,
    };
  }
  return typeof to === 'string' ? {pathname: to} : to;
}

function normalizeTo(toOrPath: string | To): To {
  if (typeof toOrPath === 'string') {
    const path: string = toOrPath;
    const queryIndex = path.indexOf('?');
    const to = {
      pathname: queryIndex > -1 ? path.slice(0, queryIndex) : path,
      search: queryIndex > -1 ? path.slice(queryIndex) : undefined,
    };
    return to;
  }
  return toOrPath;
}

/**
 * constructQueryParams returns the query parameters that should be included as part of a link
 * @param pathQuery - URL query string from the link URL/path
 * @param currentLocation - Location object representing the current loation of the app
 * @param paramsToPropagate - Array of parameter names that should be propagates from current location
 * @returns - Object with the parameters & values
 */
function constructQueryParams(
  pathQuery: string,
  currentLocation: Location,
  paramsToPropagate: string[]
) {
  const currentSearch = qs.parse(currentLocation.search);
  const pathSearch = pathQuery.length > 0 ? qs.parse(pathQuery) : {};
  const queries = pathSearch;
  for (const key of paramsToPropagate) {
    if (currentSearch[key]) {
      queries[key] = currentSearch[key];
    }
  }
  return queries;
}

/**
 * getVbucksRedirectURL constructs a URL string based on the current location search params,
 * filtering parameters to include only vbuckscard query params.
 *
 * @param location - Location object representing the current location of the app.
 * @param additionalQuery - Additional queries not grabbed from location.search (code inputted after).
 * @returns {string} - A string URL composed of the /vbuckscard path + filtered query parameters.
 *
 */
export function getVbucksRedirectURL(location: Location, additionalQuery = {}) {
  const locationQuery = qs.parse(location.search);
  const mergedQuery = {
    ...locationQuery,
    ...additionalQuery,
  };

  const filtered = {};
  const allowedKeys = [
    'code',
    'lang',
    'source',
    'sessionInvalidated',
    'utm_campaign',
    'utm_content',
    'utm_medium',
    'utm_source',
    'utm_term',
  ];

  // Written like so for ES2016 compatibility
  for (const key in mergedQuery) {
    if (Object.prototype.hasOwnProperty.call(mergedQuery, key) && allowedKeys.indexOf(key) !== -1) {
      const val = mergedQuery[key];
      filtered[key] = key === 'code' && typeof val === 'string' ? val.toUpperCase() : val;
    }
  }

  const qsString = qs.stringify(filtered);
  return `/vbuckscard${qsString ? `?${qsString}` : ''}`;
}
